/**
 * Hosted UI + Google IdP sign-in (wiki/AUTH-DESIGN.md "Hosted UI + Google
 * sign-in", issue #74/#75): a hand-rolled OAuth2 authorization-code + PKCE
 * flow against Cognito's Hosted UI, no Amplify — consistent with cognito.ts's
 * unsigned-SDK approach for the rest of the auth module.
 *
 * The verifier/state pair is stashed in sessionStorage for the single
 * redirect round-trip and consumed exactly once; `exchangeCodeForTokens`
 * hands back the same `AuthTokens` shape `srpSignIn` returns so the caller
 * can feed it straight into the existing `linkAccount` invariant.
 */
import { authConfig, type AuthConfig } from './config';
import type { AuthTokens } from './types';

const PENDING_KEY = 'simbay.oauth.pending';
const OAUTH_SCOPE = 'email openid profile';

function safeSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    // sessionStorage can throw (private mode) — the flow degrades to a
    // state/verifier mismatch on return, which is treated as an expired flow.
    return null;
  }
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** A PKCE code verifier: 32 random bytes, base64url-encoded (RFC 7636). */
export function generateCodeVerifier(): string {
  return base64url(randomBytes(32));
}

/** The S256 PKCE code challenge for a given verifier. */
export async function codeChallengeFromVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** An opaque CSRF-guard value round-tripped through the authorize redirect. */
export function generateState(): string {
  return base64url(randomBytes(16));
}

export interface AuthorizeUrlParams {
  state: string;
  codeChallenge: string;
  redirectUri: string;
}

/** Pure builder for the Hosted UI /oauth2/authorize redirect target. */
export function buildAuthorizeUrl(
  config: AuthConfig,
  { state, codeChallenge, redirectUri }: AuthorizeUrlParams,
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: redirectUri,
    identity_provider: 'Google',
    scope: OAUTH_SCOPE,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  });
  return `${config.hostedUiDomain}/oauth2/authorize?${params.toString()}`;
}

interface PendingGoogleFlow {
  codeVerifier: string;
  state: string;
}

/**
 * Generate a fresh verifier/challenge/state, stash the verifier+state for the
 * return trip, and navigate to the Hosted UI. `redirectUri` is the page
 * origin — computed at call time so it matches the registered bare-origin
 * callback_urls (`https://dev.simbay.app`, `http://localhost:3100`) on both
 * environments without a build-time env var.
 */
export async function beginGoogleSignIn(config: AuthConfig = authConfig): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const state = generateState();
  const codeChallenge = await codeChallengeFromVerifier(codeVerifier);
  const pending: PendingGoogleFlow = { codeVerifier, state };
  safeSessionStorage()?.setItem(PENDING_KEY, JSON.stringify(pending));
  const redirectUri = window.location.origin;
  window.location.assign(buildAuthorizeUrl(config, { state, codeChallenge, redirectUri }));
}

/** Read and clear the pending flow — single-use, guards against replay on reload. */
export function consumePendingGoogleFlow(): PendingGoogleFlow | null {
  const store = safeSessionStorage();
  const raw = store?.getItem(PENDING_KEY);
  if (!raw) return null;
  store?.removeItem(PENDING_KEY);
  try {
    return JSON.parse(raw) as PendingGoogleFlow;
  } catch {
    return null;
  }
}

/** Exchange an authorization code for User Pool tokens via /oauth2/token. */
export async function exchangeCodeForTokens(
  code: string,
  codeVerifier: string,
  redirectUri: string,
  config: AuthConfig = authConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<AuthTokens> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });
  const res = await fetchImpl(`${config.hostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    error?: string;
    error_description?: string;
  };
  if (!res.ok) {
    throw new Error(payload.error_description ?? payload.error ?? 'Hosted UI token exchange failed');
  }
  if (!payload.id_token || !payload.access_token) {
    throw new Error('Hosted UI token exchange returned no tokens');
  }
  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: payload.expires_in ? Date.now() + payload.expires_in * 1000 : undefined,
  };
}
